import { useState, useRef, useEffect } from 'react';
import { Sidebar, SidebarButton, Header, CircularMenuButton, Scrollable, useIsReloading, Actionbar, ActionbarLeft, ActionbarRight, useIsFormSubmitting, useHandleSubmitError } from '@/components/DashboardComponents'
import { useNavigate, useLoaderData, Form, useSubmit, useRouteLoaderData } from 'react-router'
import { Button, Textbox, Spacing, RadioGroup, RadioOption, Checkbox, TextboxList, Dropdown, DropdownOption } from '@/form/FormComponents'
import { FormProvider, FormObjectGroup, FormArrayGroup, FormDisabled, FormDataProvider, FormSubmitProvider } from '@/form/FormProvider';
import { Modal, ModalContent, ModalButtons, useErrorModal, useModal } from '@/components/Modal';
import { useContinueUnlessBlocked } from '@/systems/Blocker';
import { publicAction } from '@/data/loaders'

export function FeedbackPromptExplorer({}) {
  const navigate = useNavigate();
  const prompts = useLoaderData().data.value;
  const [tab, setTab] = useState(Object.values(prompts).length-1);
  const submitting = useIsFormSubmitting();
  const is_reloading = useIsReloading();
  const submit = useSubmit();
  useHandleSubmitError();
  const continueUnlessBlocked = useContinueUnlessBlocked();
  const [uniqueKey, setUniqueKey] = useState(0);

  function submitForm({ formatted_data, passthrough_args }) {
    submit({ id: passthrough_args.prompt_id, data: formatted_data[passthrough_args.prompt_id] }, { method: "post", encType: "application/json" });
  }

  function handleValidateFailed({ element }) {
    setTab(element.closest("[tabname]").getAttribute("tabname"));
  }

  // const [openNewPromptModal] = useModal(({ closeModal }) => {
  //   function submitThis({ formatted_data }) {
  //     closeModal();
  //     submit({ data: { name: formatted_data.name } }, { method: "post", encType: "application/json" });
  //   }

  //   return (
  //     <Modal title="New prompt">
  //       <FormProvider onSubmit={submitThis}>
  //         <FormSubmitProvider>
  //           {({handleSubmit}) => (
  //             <>
  //               <ModalContent>
  //                 <Textbox name="name" title="Prompt name" onEnter={handleSubmit} required={true} />
  //               </ModalContent>
  //               <ModalButtons>
  //                 <Button text="Cancel" className="hollow" onClick={closeModal}/>
  //                 <Button text="Submit" className="primary" onClick={handleSubmit}/>
  //               </ModalButtons>
  //             </>
  //           )}
  //         </FormSubmitProvider>
  //       </FormProvider>
  //     </Modal>
  //   )
  // });

  function handleNewPrompt(data) {
    continueUnlessBlocked(function() {
      resetFormData();
      setTab(Object.values(prompts).length);
      submit({ data: (data || {}) }, { method: "post", encType: "application/json" });
    });
  }

  let initial_data = {};
  Object.values(prompts).forEach(function(prompt) {
    initial_data[prompt._id] = prompt.data;
  });

  function resetFormData() {
    setUniqueKey(uniqueKey + 1);
  }

  return (
    <>
      <FormProvider key={Object.keys(prompts).length+"_"+uniqueKey} onSubmit={submitForm} onValidateFailed={handleValidateFailed} initial_data={initial_data} unsaved_modal="feedback_prompt_manager">
        <div className="dashboard_container" id="feedback_prompt_manager_layout">
          <header className="dashboard_topbar">
            <div className="topbar_left">
              <CircularMenuButton onClick={() => navigate("/")} icon="arrow-left"/>
              <div className="topbar_title">Feedback Prompt Manager</div>
            </div>
          </header>
          <div className="dashboard_main">
            <Sidebar>
              {Object.values(prompts).map(function(prompt, index) {
                return <SidebarButton key={prompt._id} onClick={() => {continueUnlessBlocked(function() {resetFormData();setTab(index)})}} name={(prompt.data.enabled ? (prompt.data.enabled_publicly ? "🟢 " : "🔵 ") : "⚪️ ")+(prompt.data.name || "Untitled Prompt")} selected={tab == index}/>
              })}
              <SidebarButton onClick={() => {handleNewPrompt()}} icon="plus" name="New Prompt"/>
            </Sidebar>
            <Scrollable className="dashboard_content" depencencies={[tab, Object.values(prompts).length]}>
              {Object.values(prompts).map(function(prompt, index) {
                return <FeedbackPrompt key={prompt._id} prompt={prompt} submitting={submitting} is_reloading={is_reloading} visible={tab == index} tab={index} setTab={setTab} index={index} handleNewPrompt={handleNewPrompt}/>
              })}
            </Scrollable>
          </div>
        </div>
      </FormProvider>
    </>
  )
}

function FeedbackPrompt({prompt, submitting, is_reloading, visible, tab, setTab, index, handleNewPrompt}) {
  const navigate = useNavigate();
  const [openErrorModal] = useErrorModal();
  const [openDeleteModal] = useModal(({ closeModal, setLoading }) => {
    function confirmDelete() {
      setLoading(true);
      deleteFeedbackPrompt({ id: prompt._id }).then(function(result) {
        closeModal();
        setTab(tab-1);
        navigate(".", { replace: true });
      }).catch(function(error) {
        closeModal();
        openErrorModal();
      });
    }

    return (
      <Modal title="Delete prompt?">
        <ModalContent>
          {"You can't undo this."}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Confirm" className="destructive" onClick={confirmDelete}/>
        </ModalButtons>
      </Modal>
    )
  });

  return (
    <>
      <div style={{display: (visible ? "contents" : "none")}} tabname={index}>
        <Header title={prompt.data.name || "Untitled Prompt"}>
          <Button icon="copy" text="Make a copy" onClick={() => {handleNewPrompt(prompt.data)}}/>
          <Button icon="delete-forever-outline" text="Delete" onClick={openDeleteModal}/>
        </Header>
        <div className="dashboard_content_inner nearest_scrollable_container" hidden={!visible}>
          <div className="dashboard_content_container">
            <FormObjectGroup name={prompt._id} nullify_if_all_empty={true}>
              <Textbox name="name" title="Prompt name" autofocus={visible} required={true}/>
              <Spacing/>
              <Checkbox name="enabled" text="Enabled"/>
              <FormDataProvider fields={["enabled_publicly", "enabled"]}>
                {({data}) => {
                  return <>
                    <Checkbox name="enabled_publicly" text="Publicly visible & locked-in (won't regenerate)" disabled={!data.enabled}/>
                    <Spacing/>
                    <FormDisabled obviated={!data.enabled_publicly || !data.enabled}>
                      <Textbox name="public_name" title="Public-facing name" autofocus={visible} required={data.enabled_publicly}/>
                    </FormDisabled>
                  </>
                }}
              </FormDataProvider>
              <Spacing/>
              <Dropdown title="Model" name="model" default_value="o3-mini" required={true}>
                <DropdownOption name="o3-mini" text="o3-mini"/>
                <DropdownOption name="o1" text="o1"/>
                <DropdownOption name="o1-preview" text="o1-preview"/>
              </Dropdown>
              <Spacing/>
              <Textbox name="rubric_prompt" title="Complete rubric generator prompt" placeholder="" required={true} fullWidth={true} multiline={true} height={20} hint={"Use <Scenario Information/>."}/>
              <Spacing/>
              <Textbox name="feedback_prompt" title="Complete feedback generator prompt" placeholder="" required={true} fullWidth={true} multiline={true} height={20} hint={"Use <Rubric/>, <Scenario Information/>, <Student Conversation History/>, and <Student Assessment And Plan/>. You may optionally instruct the LLM to give its final feedback inside <FinalFeedback> tags."}/>
            </FormObjectGroup>
          </div>
        </div>
        <Actionbar>
          <ActionbarLeft>
            {/* <Button icon="refresh" text="Regenerate" onClick={() => {handleRegenerateFeedback()}}/> */}
          </ActionbarLeft>
          <ActionbarRight>
            <FormSubmitProvider>
              {({handleSubmit, has_changed}) => {
                return <Button className="primary" text="Save changes" onClick={() => handleSubmit({passthrough_args: {prompt_id: prompt._id}})} disabled={submitting || !has_changed || is_reloading}/>
              }}
            </FormSubmitProvider>
          </ActionbarRight>
        </Actionbar>
      </div>
    </>
  )
}

export function loadFeedbackPrompts({ request, params }) {
  return new Promise(function(resolve, reject) {

    publicAction("loadFeedbackPrompts", {}).then(function({ data }) {
      if (data.value) {
        resolve({data: data});
      } else {
        reject({ status: 404 });
      }
    }).catch(reject);

  })
}

export function createOrUpdateFeedbackPrompt({ request, params }) {
  return new Promise(function(resolve, reject) {
    request.json().then(function(data) {

      publicAction("createOrUpdateFeedbackPrompt", {
        id: data.id,
        data: data.data
      }).then(function({ data }) {
        resolve({success: true, date: Date.now()});
      }).catch(() => {
        resolve({success: false, date: Date.now()});
      });

    })
  })
}

export function deleteFeedbackPrompt({ id }) {
  return new Promise(function(resolve, reject) {

    publicAction("deleteFeedbackPrompt", { id }).then(resolve).catch(reject);

  })
}